import { AthleteActualWorkoutDomain, Metric } from '@strength/domain';
import { useCallback, useMemo } from 'react';
import { useMutation } from '../../../../loco-sync';
import { WorkoutMutationContext } from '../../actuals/types';
import { runAthleteMutations } from '../mutation-fns/run-mutations';
import { collectMultiDayPasteCommandsByDate } from '../copy-paste/copy-paste';
import { WorkoutClipboard } from '../copy-paste/workout-clipboard';
import { WorkoutLayerCommand } from '../mutation-fns/types';
import { makeWorkoutLayerMutationFns } from '../mutation-fns/workout-draft';
import {
  AthleteActualWorkoutLayer,
  AthleteDraftWorkoutLayer,
  AthleteModifiedPublishedWorkoutLayer,
  AthletePublishedWorkoutLayer,
  BlockPublishedWorkoutLayer,
  WorkoutGroupedData,
  WorkoutWithCycleAndSession,
} from '../types';
import { useAthleteDraftWorkoutUndoRedo } from '../undo-redo/athlete';
import {
  makeActualWorkoutLayerMutationFns,
  runAthleteActualsMutations,
} from './actuals-mutations';

type BlockSummaryMutationsParams = {
  blockId: string;
  profileId: string;
  setsMetric: Metric;
  prescriptionWorkouts: WorkoutWithCycleAndSession[];
  athleteDraftLayers: AthleteDraftWorkoutLayer[];
  blockPublishedLayers: BlockPublishedWorkoutLayer[];
  athletePublishedLayers: AthletePublishedWorkoutLayer[];
  modifiedAthleteLayers: AthleteModifiedPublishedWorkoutLayer[];
  actualAthleteLayers: AthleteActualWorkoutLayer[];
};

export function useBlockSummaryMutations({
  blockId,
  profileId,
  setsMetric,
  prescriptionWorkouts,
  athleteDraftLayers,
  blockPublishedLayers,
  athletePublishedLayers,
  modifiedAthleteLayers,
  actualAthleteLayers,
}: BlockSummaryMutationsParams) {
  const mutation = useMutation();
  const {
    undo,
    redo,
    bulkMutate: prescriptionBulkMutate,
  } = useAthleteDraftWorkoutUndoRedo(blockId, profileId);

  const [prescriptionMutationFns, runPrescriptionCommands] = useMemo(() => {
    function runPrescriptionCommands(commands: WorkoutLayerCommand[]) {
      runAthleteMutations(
        commands,
        blockId,
        profileId,
        prescriptionWorkouts,
        mutation.mutate,
        prescriptionBulkMutate,
      );
    }

    const prescriptionMutationFns = makeWorkoutLayerMutationFns(
      'AthleteDraftWorkoutLayer',
      athleteDraftLayers,
      (command) => runPrescriptionCommands([command]),
      setsMetric,
      blockPublishedLayers,
    );
    return [prescriptionMutationFns, runPrescriptionCommands];
  }, [
    blockId,
    profileId,
    setsMetric,
    mutation,
    blockPublishedLayers,
    athleteDraftLayers,
    prescriptionWorkouts,
    prescriptionBulkMutate,
  ]);

  const actualsMutationFns = useMemo(() => {
    const bulkMutate = (
      commands: readonly AthleteActualWorkoutDomain.Command[],
    ) => {
      for (const command of commands) {
        mutation.mutate(command);
      }
    };

    const runActualsCommands = (
      context: WorkoutMutationContext,
      commands: WorkoutLayerCommand[],
    ) => {
      // Find the athlete layers for this workout
      const athletePublishedLayer = athletePublishedLayers.find((layer) =>
        commands.some((cmd) => cmd.identity.workoutId === layer.workoutId),
      );

      // Prefer the current layer: the query returns every version, and a
      // superseded version lacks the block version (and possibly the
      // delivery-type tags) the row was built from.
      const blockPublishedLayer =
        blockPublishedLayers.find(
          (layer) =>
            layer.isCurrent &&
            commands.some((cmd) => cmd.identity.workoutId === layer.workoutId),
        ) ??
        blockPublishedLayers.find((layer) =>
          commands.some((cmd) => cmd.identity.workoutId === layer.workoutId),
        );

      const modifiedLayer = modifiedAthleteLayers.find((layer) =>
        commands.some((cmd) => cmd.identity.workoutId === layer.workoutId),
      );

      // Fall back to the block published layer when the athlete has no
      // published layer — a day can be edited before anything was published
      // for the athlete, and the merge still needs the prescription as its
      // base layer.
      const additionalLayers =
        athletePublishedLayer || blockPublishedLayer || modifiedLayer
          ? {
              athletePublishedWorkoutLayer: athletePublishedLayer?.layer,
              blockPublishedWorkoutLayer: blockPublishedLayer?.layer,
              athleteModifiedPublishedWorkoutLayer: modifiedLayer?.layer,
            }
          : undefined;

      runAthleteActualsMutations(
        context,
        commands,
        bulkMutate,
        additionalLayers,
      );
    };
    return makeActualWorkoutLayerMutationFns(
      actualAthleteLayers,
      (context, command) => runActualsCommands(context, [command]),
    );
  }, [
    blockPublishedLayers,
    modifiedAthleteLayers,
    athletePublishedLayers,
    actualAthleteLayers,
    mutation,
  ]);

  const pastePrescription = useCallback(
    (
      clipboard: WorkoutClipboard,
      targetWorkoutId: string,
      originalTarget: WorkoutGroupedData,
    ): { ok: true } | { ok: false; error: string } => {
      const result = collectMultiDayPasteCommandsByDate(
        clipboard,
        targetWorkoutId,
        prescriptionWorkouts,
        'AthleteDraftWorkoutLayer',
        athleteDraftLayers,
        setsMetric,
        blockPublishedLayers,
        originalTarget,
      );
      if (!result.ok) return result;
      if (result.value.length > 0) {
        runPrescriptionCommands(result.value);
      }
      return { ok: true };
    },
    [
      athleteDraftLayers,
      setsMetric,
      blockPublishedLayers,
      runPrescriptionCommands,
      prescriptionWorkouts,
    ],
  );

  return {
    actualsMutationFns,
    prescriptionMutationFns,
    pastePrescription,
    undo,
    redo,
  };
}
